import { BaseAction } from "../../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { ISelectSeatAction } from "../../../../Orchestrator/Types/Interface/EventParams/SeatingAction";
import { IPassenger } from "../../../../Orchestrator/Types/Index";

export class SelectSeatAction extends BaseAction<void, ISelectSeatAction> {

    override async run(): Promise<void | null> {
        try {
            const { seatId, coachNumber, selectedCoach, passengers, selectedSeats, passengerSeatCursor, segmentIndex, allPassengers, isCompartmentSelection, compartmentSeats, passengersToMove }
                = this.params!;



            if (!passengerSeatCursor || passengerSeatCursor.length === 0) {
                console.error("Passenger seat cursor is missing or empty.");
                return;
            }

            // Create a deep copy of selectedSeats to ensure mutability, especially the nested object
            const newSelectedSeats = JSON.parse(JSON.stringify(selectedSeats));
            // Ensure the object for the current segment exists
            if (!newSelectedSeats[segmentIndex]) {
                newSelectedSeats[segmentIndex] = {};
            }
            let updatedCursor = [...passengerSeatCursor];

            //compartments
            if (isCompartmentSelection && compartmentSeats && compartmentSeats.length > 0) {
                // If any seat in this compartment is already selected, consider this click as a deselect
                const compartmentSeatKeys = compartmentSeats?.map(seat => `${coachNumber}-${seat}`);
                const occupiedSeats = compartmentSeatKeys?.filter(seatKey => !!newSelectedSeats[segmentIndex][seatKey]);

                if (occupiedSeats.length > 0) {
                    // Remove all occupied seats in the compartment
                    const passengersToFront: string[] = [];

                    occupiedSeats.forEach(seatKey => {
                        const passengerIdAtSeat = newSelectedSeats[segmentIndex][seatKey];
                        delete newSelectedSeats[segmentIndex][seatKey];

                        const passengerWithSeat = passengers?.find(p => p.id === passengerIdAtSeat);

                        if (passengerWithSeat) {
                            passengersToFront.push(passengerWithSeat.id);
                        }
                    });

                    if (passengersToFront.length > 0) {
                        const uniquePassengers = passengersToFront?.filter((id, idx) => passengersToFront?.indexOf(id) === idx);
                        updatedCursor = [
                            ...uniquePassengers,
                            ...updatedCursor?.filter(id => !uniquePassengers?.includes(id))
                        ];
                    }

                    // Update state and exit early – we only deselected seats
                    updateRecoilState("selectedSeatsAtom", newSelectedSeats);
                    updateRecoilState("passengerSeatCursorAtom", updatedCursor);
                    return;
                }

                const passengerIdsToMove = passengersToMove && passengersToMove.length > 0
                    ? passengersToMove
                    : [updatedCursor[0]];
                passengerIdsToMove.forEach(passengerId => {
                    if (newSelectedSeats[segmentIndex]) {
                        Object.keys(newSelectedSeats[segmentIndex]).forEach(seatKey => {
                            if (newSelectedSeats[segmentIndex][seatKey] === passengerId) {
                                delete newSelectedSeats[segmentIndex][seatKey];
                            }
                        });
                    }
                });

                passengerIdsToMove.forEach((passengerId, passengerIndex) => {
                    compartmentSeats.forEach((compartmentSeatId, seatIndex) => {
                        if (seatIndex % passengerIdsToMove.length === passengerIndex) {
                            const compartmentSeatKey = `${coachNumber}-${compartmentSeatId}`;
                            newSelectedSeats[segmentIndex][compartmentSeatKey] = passengerId;
                        }
                    });
                });
                passengerIdsToMove.forEach(passengerId => {
                    const index = updatedCursor.indexOf(passengerId);
                    if (index !== -1) {
                        updatedCursor.splice(index, 1);
                        updatedCursor.push(passengerId);
                    }
                });
            } else {
                // normal seat
                const fullNewSeatId = `${selectedCoach}-${seatId}`;
                
                // Find passenger by checking if they have a seat assigned in this segment's selectedSeats
                let existingPassengerWithSeat: IPassenger | undefined;
                const segmentSeats = newSelectedSeats[segmentIndex] || {};
                const targetSeatKey = `${coachNumber}-${seatId}`;
                
                if (segmentSeats[targetSeatKey]) {
                    // Find the passenger who owns this seat in this segment (by id)
                    const passengerIdAtSeat = segmentSeats[targetSeatKey];
                    existingPassengerWithSeat = passengers.find(p => p.id === passengerIdAtSeat);
                }

                if (existingPassengerWithSeat) {

                    // --- CASE 1: Unselecting a seat ---
                    // Remove from selectedSeats mapping
                    if (newSelectedSeats[segmentIndex]) {
                        delete newSelectedSeats[segmentIndex][fullNewSeatId];
                        delete newSelectedSeats[segmentIndex][`${coachNumber}-${seatId}`]; // Keep this for potential backward compatibility
                    }

                    // Update cursor: move this passenger to the front
                    updatedCursor = updatedCursor.filter(id => id !== existingPassengerWithSeat!.id);
                    updatedCursor.unshift(existingPassengerWithSeat.id);

                } else {
                    // --- CASE 2: Selecting a free seat ---

                    // Get the ID of the passenger who is next in the cursor
                    const passengerIdToAssign = updatedCursor[0];
                    const passengerToUpdate = passengers.find(p => p.id === passengerIdToAssign);
                    

                    
                    if (passengerToUpdate) {
                        // Remove any existing seat assigned to this passenger *within the current segment*
                        // Match by passenger.id (immutable) so a name change in the dropdown
                        // cannot orphan the previous seat and silently leave it reserved.
                        if (newSelectedSeats[segmentIndex]) {
                            Object.keys(newSelectedSeats[segmentIndex]).forEach(seatKey => {
                                if (newSelectedSeats[segmentIndex][seatKey] === passengerToUpdate.id) {
                                    delete newSelectedSeats[segmentIndex][seatKey];
                                }
                            });
                        }

                        // Add new seat mapping
                        // Ensure the segment object exists before assigning
                        if (!newSelectedSeats[segmentIndex]) {
                            newSelectedSeats[segmentIndex] = {};
                        }
                        newSelectedSeats[segmentIndex][fullNewSeatId] = passengerToUpdate.id;

                        // Update cursor: move this passenger to the end
                        updatedCursor.push(updatedCursor.shift()!); // Remove first element and add it to the end
                    } else {
                        console.error(`Passenger with ID ${passengerIdToAssign} not found in passengers list.`);
                        return;
                    }
                }
            }



            // Update Recoil states
            // Note: We no longer update global passenger seat properties since we manage seats per segment
            updateRecoilState("selectedSeatsAtom", newSelectedSeats);
            updateRecoilState("passengerSeatCursorAtom", updatedCursor);

        } catch (error) {
            console.error("Error in SelectSeatAction:", error);
        }
    }
}

// Create a singleton instance
export const selectSeatAction = new SelectSeatAction(); 